<script>
import IconFaceLaugh from './IconFaceLaugh.vue';
import IconFaceFrown from './IconFaceFrown.vue';
import IconFaceConfused from './IconFaceConfused.vue';
import IconFaceMeh from './IconFaceMeh.vue';
import IconFaceSmile from './IconFaceSmile.vue';
import Icon from './Icon.vue';
import IconTimesCircle from './IconTimesCircle.vue';

export default {
  components: { IconTimesCircle, Icon },
  props: {
    answer: {
      type: Number,
    },
    selected: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
    reduced: {
      type: Boolean,
    },
    isUnevaluated: {
      type: Boolean,
    }
  },

  methods: {
    getIcon(answer) {
      if (this.isUnevaluated) {
        return IconTimesCircle
      }
      switch (answer) {
        case 1:
          return IconFaceFrown;
        case 2:
          return IconFaceConfused;
        case 3:
          return IconFaceMeh;
        case 4:
          return IconFaceSmile;
        case 5:
          return IconFaceLaugh;
      }
    },

    getColor(answer) {
      if (this.isUnevaluated) {
        return 'gray-500'
      }
      switch (answer) {
        case 1:
          return 'red-700';
        case 2:
          return 'orange-600';
        case 3:
          return 'amber-300';
        case 4:
          return 'green-600';
        case 5:
          return 'green-900';
      }
    },

    getAccessibleText(answer) {
      if (this.isUnevaluated) {
        return 'Non évaluable'
      }
      switch (answer) {
        case 1:
          return 'Pas du tout satisfait';
        case 2:
          return 'Peu satisfait';
        case 3:
          return 'Moyennement satisfait';
        case 4:
          return 'Assez satisfait';
        case 5:
          return 'Très satisfait';
      }
    },

    // Avoid purging these classes
    classes() {
      return [
        'hover:border-gray-500 border-gray-500 bg-gray-500 bg-gray-500/10 text-gray-500 text-gray-500/30',
        'hover:border-red-700 border-red-700 bg-red-700 bg-red-700/10 text-red-700 text-red-700/30',
        'hover:border-orange-600 border-orange-600 bg-orange-600 bg-orange-600/10 text-orange-600 text-orange-600/30',
        'hover:border-amber-300 border-amber-300 bg-amber-300 bg-amber-300/10 text-amber-300 text-amber-300/30',
        'hover:border-green-600 border-green-600 bg-green-600 bg-green-600/10 text-green-600 text-green-600/30',
        'hover:border-green-900 border-green-900 bg-green-900 bg-green-900/10 text-green-900 text-green-900/30',
      ]
    }
  },

  computed: {
    borderColor() {
      if (this.disabled) {
        return 'border-white';
      }

      if (!this.selected) {
        return `border-white hover:border-${this.getColor(this.answer)}`;
      }

      return `border-${this.getColor(this.answer)}`;
    },

    bgColor() {
      let bgc = `bg-${this.getColor(this.answer)}`;
      if (!this.selected) {
        bgc += '/10';
      }
      if (this.disabled) {
        bgc = 'bg-gray-200/50';
      }
      return bgc;
    },

    fillColor() {
      const fill = `text-${this.getColor(this.answer)}`;

      if (this.disabled) {
        return fill + '/30';
      }

      if (this.selected) {
        return 'text-white';
      }

      return `text-${this.getColor(this.answer)}`;
    },

    rounded() {
      switch (this.answer) {
        case 1:
          return 'rounded-tr-jauge rounded-br-jauge';
        case 2:
        case 3:
        case 4:
        default:
          return '';
        case 5:
          return 'rounded-tl-jauge rounded-bl-jauge';
      }
    },

    answerIconSize() {
      if (this.reduced) {
        return 21;
      }
      return 42;
    },

    faceIcon() {
      return this.getIcon(this.answer);
    },
  },
};
</script>

<template>
  <div
    role="radio"
    tabindex="0"
    :aria-checked="selected"
    :aria-label="getAccessibleText(answer)"
    class="p-2.5 w-full border-4 justify-center items-center gap-2.5 inline-flex relative"
    :class="[
      borderColor,
      bgColor,
      rounded,
      { 'cursor-not-allowed': disabled, 'cursor-pointer': !disabled },
    ]"
    @click.stop.prevent="$emit('select')"
    @keydown.enter.stop.prevent="$emit('select')"
    @keydown.space.stop.prevent="$emit('select')"
  >
    <button
      v-if="selected && answer === 1"
      class="absolute bottom-0 left-0"
      @click.stop="$emit('unselect')"
      @keydown.enter.stop="$emit('unselect')"
      @keydown.space.stop="$emit('unselect')"
      aria-label="Désélectionner la réponse"
    >
      <Icon
        :aria-hidden="true"
        :width="answerIconSize / 2"
        :height="answerIconSize / 2"
        :fill-color="fillColor"
        no-title
        ><IconTimesCircle></IconTimesCircle
      ></Icon>
    </button>

    <Icon
      :width="answerIconSize"
      :height="answerIconSize"
      :fill-color="fillColor"
      aria-hidden="true"
      ><component :is="faceIcon"></component
    ></Icon>

    <button
      v-if="selected && answer !== 1"
      class="absolute bottom-0 right-0"
      @click.stop="$emit('unselect')"
      @keydown.enter.stop="$emit('unselect')"
      @keydown.space.stop="$emit('unselect')"
      aria-label="Désélectionner la réponse"
    >
      <icon
        :aria-hidden="true"
        :width="answerIconSize / 2"
        :height="answerIconSize / 2"
        :fill-color="fillColor"
        no-title
        ><IconTimesCircle></IconTimesCircle
      ></icon>
    </button>
  </div>
</template>